import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useQuery, useQueries, useMutation } from "react-query";
import client from "../../../../API";
import { StyledTableRow, StyledTableCell } from "../../../../Utils/styledcell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import CompanyDialog from "../info";
import {
  StyleButtonContainter,
  StyleContentWithNavBar,
} from "../../../../Utils/ThemedStyles";
import DebtDialog from "./Withdrawals/debtdialog";

const LIQUIDITY_RESERVE_PERCENT = 0.2;
const DEBT_RESERVE_PERCENT = 0.1;

const CompanyDetailsView = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPayCreatorDialogOpen, setPayCreatorDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [creditSort, setCreditSort] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [liquidityDialogOpen, setLiquidityDialogOpen] = useState(false);
  const [newLiquidity, setNewLiquidity] = useState("");
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { dialogState, openDialog } = useAlertDialog();

  const { data: companies = [] } = useQuery(["companies"], client.companies.list);
  const { data: invoices = [] } = useQuery(["invoices"], client.invoices.listAdmin);
  const { data: liquidityData } = useQuery(
    ["systemLiquidity"],
    client.creditLedger.systemLiquidity.get
  );

const {
  data: debtInstruments = [],
  refetch: fetchDebtInstruments,
  error: debtError,
} = useQuery({
  queryKey: ["debtInstruments"],
  queryFn: () => client.creditLedger.debtInstrument.list(),
  refetchOnWindowFocus: false,
});

  const { mutate: createDebt } = useMutation(
    client.creditLedger.debtInstrument.create,
    {
      onSuccess: () => {
        fetchDebtInstruments();
        openDialog("Success", "Debt instrument created");
      },
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: updateDebt } = useMutation(
    ({ id, payload }) => client.creditLedger.debtInstrument.update(id, payload),
    {
      onSuccess: () => {
        fetchDebtInstruments();
        openDialog("Success", "Debt instrument updated");
      },
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  /* ---------- computations ---------- */

  const companiesArray = Array.isArray(companies)
    ? companies
    : companies?.companies || [];
  const invoicesArray = Array.isArray(invoices)
    ? invoices
    : invoices?.invoices || [];

  const companiesWithCredit = useMemo(
    () =>
      companiesArray.filter(
        (c) => c.credit_line != null && c.credit_line !== "N/A"
      ),
    [companiesArray]
  );

  const invoiceQueries = useQueries(
    companiesWithCredit.map((c) => ({
      queryKey: ["companyInvoices", c.id],
      queryFn: () => client.companies.invoice(c.id),
      enabled: !!c.id,
      staleTime: 5 * 60 * 1000,
    }))
  );

  const pendingInvoicesByCompany = useMemo(() => {
    const map = {};
    invoiceQueries.forEach((q, idx) => {
      if (!q.data) return;
      const companyId = companiesWithCredit[idx].id;
      q.data.forEach((inv) => {
        if (inv.status === "pending" || inv.status === "approved") {
          map[companyId] =
            (map[companyId] || 0) + parseFloat(inv.amount_due || 0);
        }
      });
    });
    return map;
  }, [invoiceQueries, companiesWithCredit]);

  const pendingInvoicesCountCred = useMemo(
    () =>
      invoiceQueries.reduce(
        (n, q) =>
          n +
          (q.data
            ? q.data.filter(
                (inv) => inv.status === "pending" || inv.status === "approved"
              ).length
            : 0),
        0
      ),
    [invoiceQueries]
  );

  const pendingInvoicesSumCred = useMemo(
    () =>
      Object.values(pendingInvoicesByCompany).reduce(
        (s, v) => s + parseFloat(v),
        0
      ),
    [pendingInvoicesByCompany]
  );

  const pendingInvoicesCountTotal = invoicesArray.filter(
    (inv) => inv.status === "pending"
  ).length;

  const pendingInvoicesSumTotal = invoicesArray
    .filter((inv) => inv.status === "pending" || inv.status === "approved")
    .reduce((s, inv) => s + parseFloat(inv.amount_due || 0), 0);

  const totalCompanies = companiesArray.length;

  const debtOutstanding = debtInstruments.reduce(
    (s, d) => s + parseFloat(d.balance || 0),
    0
  );

  const debtCapacity = debtInstruments.reduce((s, d) => {
    if (d.credit_limit != null)
      s += parseFloat(d.credit_limit) - parseFloat(d.balance || 0);
    return s;
  }, 0);

  const totalCreditAllocated = companiesArray
    .filter((c) => c.name !== "TCC")
    .reduce((s, c) => s + parseFloat(c.credit_line || 0), 0);

  const totalLiquidity = liquidityData
    ? parseFloat(liquidityData.total_liquidity)
    : 0;

  const overleveraged = totalLiquidity - totalCreditAllocated;
  const overleveragedIsNegative = overleveraged < 0;

  const liquidityBuffer = totalLiquidity * LIQUIDITY_RESERVE_PERCENT;
  const debtBuffer = debtCapacity * DEBT_RESERVE_PERCENT;

  const creditAvailableToDistribute = Math.max(
    0,
    Math.min(
      totalLiquidity - liquidityBuffer - totalCreditAllocated,
      debtCapacity - debtBuffer
    )
  );

  const formatAmount = (amt) =>
    amt == null
      ? "N/A"
      : `$${parseFloat(amt).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  let displayedCompanies = [...companiesArray];
  if (searchTerm.trim()) {
    displayedCompanies = displayedCompanies.filter((c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  if (vendorFilter === "true")
    displayedCompanies = displayedCompanies.filter((c) => c.vendor_system);
  if (vendorFilter === "false")
    displayedCompanies = displayedCompanies.filter((c) => !c.vendor_system);
  if (creditSort === "desc")
    displayedCompanies.sort(
      (a, b) => parseFloat(b.credit_line || 0) - parseFloat(a.credit_line || 0)
    );
  if (creditSort === "asc")
    displayedCompanies.sort(
      (a, b) => parseFloat(a.credit_line || 0) - parseFloat(b.credit_line || 0)
    );

  const { mutate: updateSystemLiquidity } = useMutation(
    (total_liquidity) =>
      client.creditLedger.systemLiquidity.update({ total_liquidity }),
    {
      onSuccess: () => {
        setLiquidityDialogOpen(false);
        setNewLiquidity("");
      },
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deleteCompanies } = useMutation(client.companies.delete, {
    onSuccess: () => {
      setSelectedIds([]);
    },
    onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
  });

  const handleSelectItem = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleShareLink = (name) => {
    const url = `https://www.useblitz.co/register/${name}`;
    navigator.clipboard.writeText(url).then(() => window.open(url, "_blank"));
  };

  const handleLiquidityUpdate = () => {
    const parsed = parseFloat(newLiquidity);
    if (isNaN(parsed)) return openDialog("Error", "Enter a valid number.");
    updateSystemLiquidity(parsed);
  };

  return (
    <Box sx={StyleContentWithNavBar}>
      <AlertDialog alertState={dialogState} />

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <StatCard title="Total Companies" value={totalCompanies} />
        <StatCard
          title="Credit Available to Distribute"
          value={formatAmount(creditAvailableToDistribute)}
        />
        <StatCard
          title="Total Credit Allocated"
          value={formatAmount(totalCreditAllocated)}
        />
        <StatCard
          title="Pending Invoices (All)"
          value={pendingInvoicesCountTotal}
        />
        <StatCard
          title="Accounts Receivable (All)"
          value={formatAmount(pendingInvoicesSumTotal)}
        />
        <StatCard
          title="Pending Invoices (Cred-Line)"
          value={pendingInvoicesCountCred}
        />
        <StatCard
          title="A/R (Cred-Line)"
          value={formatAmount(pendingInvoicesSumCred)}
        />
        <StatCard
          title="Total Liquidity"
          value={liquidityData ? formatAmount(totalLiquidity) : "Loading…"}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLiquidityDialogOpen(true)}
              sx={{ mt: 1 }}
            >
              Update Liquidity
            </Button>
          }
        />
        <StatCard
          title="Total Overleveraged"
          value={formatAmount(overleveraged)}
          sx={{
            border: `2px solid ${overleveragedIsNegative ? "red" : "green"}`,
          }}
        />
        <StatCard title="Debt Outstanding" value={formatAmount(debtOutstanding)} />
        <StatCard title="Debt Capacity" value={formatAmount(debtCapacity)} />
      </Box>

      <Box sx={StyleButtonContainter}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setPayCreatorDialogOpen(true)}
        >
          Add Company
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedIds.length !== 1}
          onClick={() => setPayCreatorDialogOpen(true)}
        >
          Edit Company
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedIds.length}
          onClick={() => deleteCompanies({ companyIds: selectedIds })}
        >
          Delete Company(s)
        </Button>
        <TextField
          variant="outlined"
          size="small"
          label="Search by Company Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ ml: 2 }}
        />
        <FormControl sx={{ ml: 2, minWidth: 150 }}>
          <InputLabel>Vendor System</InputLabel>
          <Select
            label="Vendor System"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Vendor System = True</MenuItem>
            <MenuItem value="false">Vendor System = False</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ ml: 2, minWidth: 150 }}>
          <InputLabel>Sort Credit</InputLabel>
          <Select
            label="Sort Credit"
            value={creditSort}
            onChange={(e) => setCreditSort(e.target.value)}
            size="small"
          >
            <MenuItem value="">No Sort</MenuItem>
            <MenuItem value="desc">High to Low</MenuItem>
            <MenuItem value="asc">Low to High</MenuItem>
          </Select>
        </FormControl>
        <Tabs
          value={viewMode}
          onChange={(e, v) => setViewMode(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ ml: 2 }}
        >
          <Tab label="Grid" icon={<GridViewIcon />} value="grid" />
          <Tab label="List" icon={<ListIcon />} value="list" />
        </Tabs>
      </Box>

      {viewMode === "grid" ? (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {displayedCompanies.map((company) => {
            const pendingAmt = pendingInvoicesByCompany[company.id] || 0;
            const totalCredit =
              parseFloat(company.credit_line || 0) + pendingAmt;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={company.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 6 },
                    border: selectedIds.includes(company.id)
                      ? "2px solid #1976d2"
                      : undefined,
                  }}
                  onClick={() => {
                    setSelectedCompany(company);
                    setDialogOpen(true);
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        src={company.phphref || ""}
                        alt={company.name}
                        sx={{ bgcolor: "#1976d2" }}
                      >
                        {company.name?.[0] || "C"}
                      </Avatar>
                    }
                    title={company.name}
                    subheader={`Status: ${company.account_status}`}
                    action={
                      <Checkbox
                        color="primary"
                        checked={selectedIds.includes(company.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectItem(company.id);
                        }}
                      />
                    }
                  />
                  <CardContent>
                    <Typography variant="body2">
                      <strong>Balance:</strong> {formatAmount(company.balance)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Credit Line:</strong>{" "}
                      {formatAmount(company.credit_line)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Pending Invoice Amount:</strong>{" "}
                      {formatAmount(pendingAmt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Credit:</strong>{" "}
                      {formatAmount(totalCredit)}
                    </Typography>
                    <Tooltip title="Share Onboard Link">
                      <IconButton
                        color="primary"
                        onClick={() => handleShareLink(company.name)}
                        size="small"
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell align="center">ID</StyledTableCell>
                <StyledTableCell align="center">Name</StyledTableCell>
                <StyledTableCell align="center">Balance</StyledTableCell>
                <StyledTableCell align="center">Credit Line</StyledTableCell>
                <StyledTableCell align="center">
                  Pending Invoice Amount
                </StyledTableCell>
                <StyledTableCell align="center">Total Credit</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedCompanies.map((company) => {
                const pendingAmt = pendingInvoicesByCompany[company.id] || 0;
                const totalCredit =
                  parseFloat(company.credit_line || 0) + pendingAmt + company.balance;
                return (
                  <StyledTableRow
                    key={company.id}
                    sx={{
                      backgroundColor: selectedIds.includes(company.id)
                        ? "rgba(25,118,210,0.1)"
                        : undefined,
                    }}
                  >
                    <StyledTableCell align="center">
                      <Checkbox
                        color="primary"
                        checked={selectedIds.includes(company.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectItem(company.id);
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {company.id}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {company.name}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {formatAmount(company.balance)}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {formatAmount(company.credit_line)}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {formatAmount(pendingAmt)}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {formatAmount(totalCredit)}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip title="Share Onboard Link">
                        <IconButton
                          color="primary"
                          onClick={() => handleShareLink(company.name)}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CompanyDialog
        open={isPayCreatorDialogOpen}
        info={
          selectedIds.length
            ? companiesArray.find((c) => c.id === selectedIds[0])
            : undefined
        }
        onClose={(refresh) => {
          setPayCreatorDialogOpen(false);
          if (refresh) {
            /* refresh list if needed */
          }
        }}
      />

      <CompanyDialog
        open={dialogOpen}
        info={selectedCompany}
        onClose={(refresh) => {
          setDialogOpen(false);
          setSelectedCompany(null);
          if (refresh) {
            /* refresh list if needed */
          }
        }}
      />

      <Dialog
        open={liquidityDialogOpen}
        onClose={() => setLiquidityDialogOpen(false)}
      >
        <DialogTitle>Update System Liquidity</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Total Liquidity"
            type="number"
            fullWidth
            variant="outlined"
            value={newLiquidity}
            onChange={(e) => setNewLiquidity(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiquidityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLiquidityUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <DebtDialog
        open={debtDialogOpen}
        instruments={debtInstruments}
        onCreate={() => {createDebt}}
        onUpdate={() => {updateDebt}}
        onClose={() => setDebtDialogOpen(false)}
      />
    </Box>
  );
};

const StatCard = ({ title, value, action, sx = {} }) => (
  <Card sx={{ minWidth: 200, ...sx }}>
    <CardContent>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="h5">{value}</Typography>
      {action}
    </CardContent>
  </Card>
);

export default CompanyDetailsView;
